/**
 * Fit Builder — Milktub
 *
 * Renders as a row in the PDP accordion. Opening the row is what triggers the
 * build, so there is no separate button.
 *
 * Talks to the Milktub fit engine for the fit itself, and to Shopify for
 * everything Shopify already does: `/cart/add.js` for the cart,
 * `/search/suggest.json` for the anchor picker, `/products/{handle}.js` for
 * market-correct prices, live availability and colour changes. No cart state is
 * kept here — the theme's own cart components own that, and we hand off by
 * dispatching the same CartAddEvent the product form does.
 */

import { CartAddEvent } from '@theme/events';
import { formatMoney, convertMoneyToMinorUnits } from '@theme/money-formatting';
import { saveProfile, hydrateProfile, readWishlist, productsByHandle, suggestedForBrands, newArrivals } from '@theme/fit-profile';

const SUGGEST_DEBOUNCE_MS = 220;
const ADDED_FLASH_MS = 2200;
/** Engine caps `keep` at 3, so the lock UI has to cap there too. */
const MAX_LOCKS = 3;
const ALT_COLUMNS = 3;
/**
 * Pills that fit on one line of a two-up card (~148px on a 375px phone). Four
 * exactly fill the row, so when a +N is needed it takes the fourth slot and only
 * three sizes show — otherwise the +N wraps and orphans a whole line.
 */
const SIZES_PER_ROW = 4;

/** Order the chips are offered in: the core outfit first, extras after. */
const ALL_SLOTS = ['top', 'bottom', 'footwear', 'layer', 'headwear', 'accessory'];

/**
 * Selected when the customer has saved nothing. Headwear and accessories are
 * additions someone opts into; including them by default made every fit five or
 * six pieces, where the brief is a three to four piece outfit.
 */
const DEFAULT_SLOTS = ['top', 'bottom', 'footwear', 'layer'];

/** @type {Record<string, string>} */
const SLOT_LABELS = {
  top: 'Top',
  bottom: 'Bottom',
  footwear: 'Footwear',
  layer: 'Layer',
  headwear: 'Headwear',
  accessory: 'Accessory',
};

const ICON_LOCK =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>';
const ICON_SWAP =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 8h13l-3.5-3.5M21 16H8l3.5 3.5"/></svg>';

let memoryVisitorId = '';

/**
 * Stable per-browser id for the engine's rate limit. Not used for anything else,
 * and never sent to Shopify.
 * @returns {string}
 */
function visitorId() {
  try {
    let id = localStorage.getItem('fit-builder-visitor');
    if (!id) {
      id = uid();
      localStorage.setItem('fit-builder-visitor', id);
    }
    return id;
  } catch {
    // Private browsing throws on write. A session-lived id still makes the
    // limit mean something.
    if (!memoryVisitorId) memoryVisitorId = uid();
    return memoryVisitorId;
  }
}

function uid() {
  if (crypto.randomUUID) return crypto.randomUUID();
  return `fb-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function esc(value) {
  return String(value ?? '').replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] ?? char
  );
}

/**
 * The engine returns variant GIDs; the cart wants the numeric id.
 * @param {string} gid
 * @returns {string}
 */
function variantId(gid) {
  const match = /(\d+)\s*$/.exec(String(gid ?? ''));
  return match ? match[1] : '';
}

class FitBuilder extends HTMLElement {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #suggestTimer;
  /** @type {AbortController | undefined} */
  #inflight;
  /** @type {Record<string, string>} */
  #strings = {};
  /** Handles the customer locked. Their slots survive a shuffle. */
  #locked = new Set();
  /** Everything already shown, so a shuffle brings back something new. */
  #seen = new Set();
  /** handle -> chosen variant GID. Set once per piece so price and cart agree. */
  #chosen = new Map();
  /** Handles whose size list is expanded past the +N. */
  #expanded = new Set();
  /** Slots the customer wants filled. Null until they narrow it themselves. */
  #slots = null;
  /** The slot the anchor itself occupies, so its chip can be disabled. */
  #anchorSlot = null;
  /** @type {any} */
  #fit = null;
  /** Groups the cart lines of one fit so the discount can find them. */
  #fitId = '';
  #anchor = '';
  #busy = false;
  #built = false;

  connectedCallback() {
    this.#strings = this.#readStrings();
    this.#anchor = this.dataset.anchor || '';

    this.querySelector('[data-fb-toggle]')?.addEventListener('click', () => this.#toggle());
    this.#wireSearch();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', (e) => {
      if (e.target instanceof HTMLInputElement && e.target.hasAttribute('data-fb-pref-brand')) this.#syncBrandLabel();
    });
    this.addEventListener('keydown', this.#onKeydown);

    if (this.dataset.startOpen === 'true' || this.dataset.layout === 'section') this.#toggle(true);
    if (this.dataset.layout === 'section') void this.#initPage();
  }

  disconnectedCallback() {
    this.#inflight?.abort();
    clearTimeout(this.#suggestTimer);
  }

  /** @returns {Record<string, string>} */
  #readStrings() {
    const template = this.querySelector('[data-fb-strings]');
    if (!(template instanceof HTMLTemplateElement)) return {};
    try {
      return JSON.parse(template.content.textContent || '{}');
    } catch {
      return {};
    }
  }

  get #out() {
    return /** @type {HTMLElement} */ (this.querySelector('[data-fb-out]'));
  }

  get #panel() {
    return /** @type {HTMLElement} */ (this.querySelector('[data-fb-panel]'));
  }

  get #currency() {
    return this.dataset.currency || 'USD';
  }

  /** @param {number} minorUnits */
  #money(minorUnits) {
    return formatMoney(minorUnits, this.dataset.moneyFormat || '${{amount}}', this.#currency);
  }

  // -------------------------------------------------------------- accordion

  /**
   * Opening the row is the build trigger — expanding already says what the
   * customer wants, so a second button would be a step in the way.
   * @param {boolean} [force]
   */
  #toggle(force) {
    const header = this.querySelector('[data-fb-toggle]');
    // The open-section layout has no header: the panel is always open, and only
    // the build-on-first-open behaviour below still applies.
    const open = header instanceof HTMLElement
      ? (force ?? header.getAttribute('aria-expanded') !== 'true')
      : true;
    header?.setAttribute('aria-expanded', String(open));
    this.#panel.classList.toggle('is-open', open);

    if (open && !this.#built && this.#anchor) {
      this.#built = true;
      this.#build();
    }
  }

  // ---------------------------------------------------------------- anchoring

  #wireSearch() {
    const input = this.querySelector('[data-fb-input]');
    if (!(input instanceof HTMLInputElement)) return;

    input.addEventListener('input', () => {
      clearTimeout(this.#suggestTimer);
      const term = input.value.trim();
      if (term.length < 2) return this.#closeSuggest();
      this.#suggestTimer = setTimeout(() => this.#suggest(term), SUGGEST_DEBOUNCE_MS);
    });

    // Delayed so a click on a suggestion lands before the list closes.
    input.addEventListener('blur', () => setTimeout(() => this.#closeSuggest(), 150));
  }

  /** @param {string} term */
  async #suggest(term) {
    // Shopify's own predictive search: better-ranked than anything we would
    // write, and it costs us nothing.
    const url = new URL('/search/suggest.json', window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '6');

    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return this.#closeSuggest();
      const data = await response.json();
      this.#renderSuggest(data?.resources?.results?.products ?? []);
    } catch {
      this.#closeSuggest();
    }
  }

  /** @param {Array<any>} products */
  #renderSuggest(products) {
    const list = this.querySelector('[data-fb-ac]');
    const input = this.querySelector('[data-fb-input]');
    if (!(list instanceof HTMLElement) || !(input instanceof HTMLInputElement)) return;
    if (!products.length) return this.#closeSuggest();

    list.innerHTML = products
      .map(
        (product) => `
        <button type="button" class="fb__ac-item" role="option"
                data-fb-pick="${esc(product.handle)}" data-fb-title="${esc(product.title)}">
          ${
            product.image
              ? `<img src="${esc(product.image)}" alt="" width="36" height="36" loading="lazy">`
              : '<span class="fb__ac-blank"></span>'
          }
          <span class="fb__ac-title">${esc(product.title)}</span>
        </button>`
      )
      .join('');
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  #closeSuggest() {
    const list = this.querySelector('[data-fb-ac]');
    if (list instanceof HTMLElement) {
      list.hidden = true;
      list.innerHTML = '';
    }
    this.querySelector('[data-fb-input]')?.setAttribute('aria-expanded', 'false');
  }

  // ------------------------------------------------------------------ engine

  /** @param {{ instruction?: string, shuffle?: boolean }} [options] */
  async #build(options = {}) {
    if (!this.#anchor || this.#busy) return;
    this.#busy = true;
    this.#inflight?.abort();
    this.#inflight = new AbortController();

    const refining = Boolean(options.instruction);
    if (refining || options.shuffle) {
      this.#out.classList.add('fb__out--pending');
    } else {
      this.#renderSkeleton();
    }

    /** @type {Record<string, unknown>} */
    const payload = { entry: 'history', anchor_handle: this.#anchor };
    if (this.#slots?.length) payload.slots = this.#slots;
    if (this.#locked.size) payload.keep = [...this.#locked].slice(0, MAX_LOCKS);
    if (options.shuffle) {
      // Exclude what has been shown, minus the locks — the engine has to be
      // allowed to hand a locked piece back.
      payload.exclude = [...this.#seen].filter((handle) => !this.#locked.has(handle)).slice(0, 50);
    }
    if (refining) {
      payload.instruction = options.instruction;
      payload.current = (this.#fit?.pieces ?? []).map((/** @type {any} */ piece) => piece.handle);
    }

    try {
      const fit = await this.#post('/fit', payload);
      await this.#localise(fit.pieces ?? []);
      this.#fit = fit;
      this.#fitId = uid();
      this.#anchorSlot = fit.anchor?.slot ?? null;
      this.#renderChips();
      for (const piece of fit.pieces ?? []) this.#seen.add(piece.handle);
      // Drop locks the engine did not honour, or the lock UI lies.
      const live = new Set((fit.pieces ?? []).map((/** @type {any} */ piece) => piece.handle));
      for (const handle of [...this.#locked]) if (!live.has(handle)) this.#locked.delete(handle);
      this.#render();
    } catch (error) {
      if (/** @type {any} */ (error)?.name === 'AbortError') return;
      this.#renderError(/** @type {any} */ (error)?.message);
    } finally {
      this.#busy = false;
      this.#out.classList.remove('fb__out--pending');
    }
  }

  /**
   * @param {string} path
   * @param {Record<string, unknown>} body
   */
  async #post(path, body) {
    /** @type {Record<string, string>} */
    const headers = { 'Content-Type': 'application/json', 'X-Fit-Visitor': visitorId() };
    // Only set while the engine is gated for testing; cleared before launch.
    if (this.dataset.accessKey) headers['X-Fit-Key'] = this.dataset.accessKey;

    const response = await fetch(`${this.dataset.endpoint}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: this.#inflight?.signal,
    });

    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(data?.error || this.#strings.errGeneric);
    return data;
  }

  /**
   * Prices from the engine are in the shop's base currency, because the catalog
   * APIs it calls have no market context. This store sells into markets with
   * currency conversion — an Indian visitor is charged INR at roughly 96x — so
   * the engine's number would be out by orders of magnitude.
   * @param {string} amount - major units, base currency
   * @returns {number} minor units, presentment currency
   */
  #fallbackMinor(amount) {
    const base = convertMoneyToMinorUnits(String(amount), this.#currency) ?? 0;
    const rate = Number(window.Shopify?.currency?.rate ?? 1);
    return Math.round(base * (Number.isFinite(rate) && rate > 0 ? rate : 1));
  }

  /**
   * Replace engine prices with Shopify's own, which are market-aware and already
   * rounded to the market's rules, so the widget matches the product page and
   * the cart to the penny. Also refreshes availability, catching anything that
   * sold out since the engine read the catalog.
   * @param {any[]} pieces
   */
  async #localise(pieces) {
    await Promise.all(
      pieces.map(async (piece) => {
        try {
          const response = await fetch(`/products/${encodeURIComponent(piece.handle)}.js`, {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error('unavailable');
          const product = await response.json();

          piece.priceMinor = product.price;
          piece.image = piece.image || product.featured_image;
          // Shopify is authoritative on variants; the engine's copy is a snapshot.
          piece.variants = (product.variants ?? []).map((/** @type {any} */ variant) => ({
            id: `gid://shopify/ProductVariant/${variant.id}`,
            title: variant.title,
            available: variant.available,
            priceMinor: variant.price,
          }));

          this.#setDefaultVariant(piece);
        } catch {
          piece.priceMinor = this.#fallbackMinor(piece.price);
        }
      })
    );
  }

  /**
   * Pick the default variant once, so the price on the card, the total in the
   * footer and the line that reaches the cart are all the same variant. Sneakers
   * are priced per size here, so showing the product's lowest price while adding
   * the first available size understates the total.
   * @param {any} piece
   */
  #setDefaultVariant(piece) {
    if (this.#chosen.has(piece.handle)) return;
    const preferred = (piece.variants ?? []).find((/** @type {any} */ variant) => variant.available);
    if (preferred) this.#chosen.set(piece.handle, preferred.id);
  }

  /**
   * The price the customer is actually being offered: the selected variant's,
   * falling back to the product's.
   * @param {any} piece
   * @returns {number}
   */
  #priceMinorFor(piece) {
    const chosen = this.#chosen.get(piece.handle);
    const variant = (piece.variants ?? []).find((/** @type {any} */ v) => v.id === chosen);
    return variant?.priceMinor ?? piece.priceMinor ?? 0;
  }

  // --------------------------------------------------------------- rendering

  #renderSkeleton() {
    const card = '<div class="fb__sk-card"><i class="is-media"></i><i class="is-line"></i><i class="is-line is-short"></i></div>';
    this.#out.innerHTML = `
      <p class="fb__intro">${esc(this.#strings.building)}</p>
      <div class="fb__sk" aria-hidden="true">${card.repeat(4)}</div>`;
  }

  /**
   * Category chips. Rendered only after a fit exists, because the anchor's own
   * slot is not known until the engine reports it, and offering a chip we then
   * have to disable reads as a bug.
   *
   * The selection starts as whatever the engine chose, so the chips describe the
   * fit on screen rather than presenting an empty form the customer has to fill
   * in before they get anything.
   */
  #renderChips() {
    const host = this.querySelector('[data-fb-slots]');
    const chips = this.querySelector('[data-fb-chips]');
    if (!(host instanceof HTMLElement) || !(chips instanceof HTMLElement)) return;
    if (this.dataset.allowSlots !== 'true') return;

    const active = new Set(this.#slots ?? (this.#fit?.pieces ?? []).map((/** @type {any} */ p) => p.slot));

    chips.innerHTML = ALL_SLOTS.map((slot) => {
      // The anchor already fills this slot, so styling it around a second one
      // makes no sense. The engine refuses it too; this just says so up front.
      const isAnchor = slot === this.#anchorSlot;
      const on = !isAnchor && active.has(slot);
      return `
        <button type="button" class="fb__chip" data-fb-slot="${esc(slot)}"
                aria-pressed="${on}" ${isAnchor ? 'disabled' : ''}
                ${isAnchor ? `title="${esc(this.#strings.anchorSlotNote)}"` : ''}>
          ${esc(SLOT_LABELS[slot] || slot)}
        </button>`;
    }).join('');
    host.hidden = false;
  }

  /**
   * @param {HTMLElement} chip
   */
  #toggleSlot(chip) {
    const slot = chip.dataset.fbSlot;
    if (!slot || chip.hasAttribute('disabled')) return;

    // First interaction inherits the current fit, so toggling one category off
    // keeps the rest rather than starting from nothing.
    const next = new Set(this.#slots ?? (this.#fit?.pieces ?? []).map((/** @type {any} */ p) => p.slot));
    if (next.has(slot)) next.delete(slot);
    else next.add(slot);
    next.delete(this.#anchorSlot ?? '');

    if (next.size === 0) {
      this.#flash(chip.parentElement ?? chip, 'Keep at least one category.');
      return;
    }

    this.#slots = ALL_SLOTS.filter((s) => next.has(s));
    this.#renderChips();
    // Locks belong to the old fit; a different set of categories invalidates them.
    this.#locked.clear();
    this.#build();
  }

  // ------------------------------------------------------------- page layout

  /**
   * Standalone page: fill both feeds and reflect saved preferences in the rail.
   * The account copy is merged in first so a returning customer on a new device
   * sees their own settings rather than a blank form.
   */
  /** Filter the brand list as the customer types. 74 options is too many to scan. */
  #wireBrandFilter() {
    const filter = this.querySelector('[data-fb-drop-filter]');
    if (!(filter instanceof HTMLInputElement)) return;
    filter.addEventListener('input', () => {
      const term = filter.value.trim().toLowerCase();
      for (const opt of this.querySelectorAll('.fb-drop__opt')) {
        const text = (opt.textContent || '').toLowerCase();
        opt.toggleAttribute('hidden', Boolean(term) && !text.includes(term));
      }
    });
  }

  async #initPage() {
    this.#wireBrandFilter();
    const profile = await hydrateProfile(this.dataset.proxy || '');
    this.#applyProfileToRail(profile);
    if (profile.slots?.length) this.#slots = profile.slots;
    await Promise.all([this.#fillWishlistFeed(), this.#fillSuggestedFeed(profile.brands ?? [])]);
  }

  /** @param {any} profile */
  #applyProfileToRail(profile) {
    const set = (attr, values) => {
      const chosen = new Set(values ?? []);
      for (const el of this.querySelectorAll(`[${attr}]`)) {
        // Slots default to on; everything else defaults to off, because an
        // empty brand list means "no preference", not "no brands".
        const value = el.getAttribute(attr) ?? '';
        const on =
          attr === 'data-fb-pref-slot' && !values?.length ? DEFAULT_SLOTS.includes(value) : chosen.has(value);
        if (el instanceof HTMLInputElement) el.checked = on;
        else el.setAttribute('aria-pressed', String(on));
      }
    };
    set('data-fb-pref-slot', profile.slots);
    set('data-fb-pref-brand', profile.brands);
    set('data-fb-pref-colour', profile.colours);
    set('data-fb-pref-style', profile.styles);
    this.#syncBrandLabel();
    const consent = this.querySelector('[data-fb-consent]');
    if (consent instanceof HTMLInputElement) consent.checked = Boolean(profile.consent);
  }

  /**
   * Chosen values for one preference group. Brands are checkboxes inside a
   * dropdown, everything else is a pressed chip, so both shapes are read here.
   * @param {string} attr
   */
  #railValues(attr) {
    return [...this.querySelectorAll(`[${attr}]`)]
      .filter((el) =>
        el instanceof HTMLInputElement ? el.checked : el.getAttribute('aria-pressed') === 'true'
      )
      .map((el) => el.getAttribute(attr) ?? '')
      .filter(Boolean);
  }

  /** Summarise the brand selection on the closed dropdown. */
  #syncBrandLabel() {
    const label = this.querySelector('[data-fb-drop-label]');
    if (!(label instanceof HTMLElement)) return;
    const chosen = this.#railValues('data-fb-pref-brand');
    label.textContent =
      chosen.length === 0
        ? this.#strings.brandsAll
        : chosen.length <= 2
          ? chosen.join(', ')
          : `${chosen.length} brands`;
  }

  async #fillWishlistFeed() {
    const section = this.querySelector('[data-fb-feed="wishlist"]');
    if (!(section instanceof HTMLElement)) return;
    // Newest first: the most recently hearted piece is the likeliest anchor.
    const handles = readWishlist().slice(-8).reverse();
    if (!handles.length) return;
    const products = await productsByHandle(handles);
    if (!products.length) return;
    this.#renderFeed(section, products.slice(0, 4).map((p) => ({
      handle: p.handle, title: p.title, vendor: p.vendor, image: p.image,
      price: this.#money(p.priceMinor),
    })));
  }

  /** @param {string[]} brands */
  async #fillSuggestedFeed(brands) {
    const section = this.querySelector('[data-fb-feed="suggested"]');
    if (!(section instanceof HTMLElement)) return;
    const shown = readWishlist();

    // No saved brands means a first-time visitor, and the left column would
    // otherwise be a search box above a screen of nothing. Fall back to new
    // arrivals and relabel the heading, rather than presenting a generic feed as
    // though it were personalised.
    if (!brands.length) {
      const fallback = await newArrivals();
      if (!fallback.length) return;
      const heading = section.querySelector('.fb-page__h');
      if (heading) heading.textContent = this.#strings.newIn;
      const hint = section.querySelector('.fb-page__hint');
      if (hint) hint.textContent = this.#strings.newInHint;
      this.#renderFeed(
        section,
        fallback.map((p) => ({
          handle: p.handle, title: p.title, vendor: p.vendor, image: p.image,
          price: this.#money(p.priceMinor),
        }))
      );
      return;
    }

    const products = await suggestedForBrands(brands, shown);
    if (!products.length) return;
    // Predictive search returns price as a bare string in the presentment
    // currency, with no symbol. Run it through the same formatter as everything
    // else so the two feeds do not sit side by side reading "£158.00" and
    // "113.00".
    this.#renderFeed(
      section,
      products.map((item) => ({
        ...item,
        price: this.#money(convertMoneyToMinorUnits(String(item.price), this.#currency) ?? 0),
      }))
    );
  }

  /**
   * @param {HTMLElement} section
   * @param {Array<{handle:string,title:string,vendor:string,image:string,price:string}>} items
   */
  #renderFeed(section, items) {
    const row = section.querySelector('[data-fb-feed-row]');
    if (!(row instanceof HTMLElement)) return;
    row.innerHTML = items
      .map(
        (item) => `
        <div class="fb-feed__it">
          <a class="fb-feed__media" href="/products/${esc(item.handle)}">
            ${item.image ? `<img src="${esc(item.image)}" alt="" loading="lazy" width="240" height="240">` : '<span class="fb__blank"></span>'}
          </a>
          ${item.vendor ? `<span class="fb-feed__brand">${esc(item.vendor)}</span>` : ''}
          <a class="fb-feed__name" href="/products/${esc(item.handle)}">${esc(item.title)}</a>
          <span class="fb-feed__price">${esc(item.price)}</span>
          <button type="button" class="fb-feed__build" data-fb-anchor="${esc(item.handle)}">
            ${esc(this.#strings.buildFromThis)}
          </button>
        </div>`
      )
      .join('');
    section.hidden = false;
  }

  /** Save the rail, then rebuild so the change is visible rather than merely stored. */
  async #savePreferences() {
    const consent = this.querySelector('[data-fb-consent]');
    const profile = await saveProfile(
      {
        slots: this.#railValues('data-fb-pref-slot'),
        brands: this.#railValues('data-fb-pref-brand'),
        colours: this.#railValues('data-fb-pref-colour'),
        styles: this.#railValues('data-fb-pref-style'),
        consent: consent instanceof HTMLInputElement ? consent.checked : false,
      },
      this.dataset.proxy || ''
    );

    const note = this.querySelector('[data-fb-pref-note]');
    if (note instanceof HTMLElement) {
      note.textContent = profile.consent
        ? 'Saved to your account.'
        : 'Saved on this device. Tick the box to keep them on your account.';
    }

    this.#slots = profile.slots.length ? profile.slots : null;
    void this.#fillSuggestedFeed(profile.brands);

    // The button is the page's primary action, so it has to do something even
    // before a piece has been chosen. It used to no-op whenever there was no
    // anchor, which on this page is every first click.
    if (!this.#anchor) this.#anchor = this.#fallbackAnchor();
    if (this.#anchor) {
      this.#out.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      void this.#build();
    } else {
      // Nothing on the page to build from, which means the feeds are empty too.
      // Point at the one control that can fix it rather than failing silently.
      const input = this.querySelector('[data-fb-input]');
      if (input instanceof HTMLInputElement) input.focus();
      if (note instanceof HTMLElement) note.textContent = this.#strings.pickOne;
    }
  }

  /**
   * A piece to build around when the customer has not chosen one. Their wishlist
   * first, since that is the strongest signal on the page, then whatever the
   * feeds are already showing them.
   * @returns {string}
   */
  #fallbackAnchor() {
    const wishlisted = readWishlist();
    if (wishlisted.length) return wishlisted[wishlisted.length - 1];
    const first = this.querySelector('[data-fb-anchor]');
    return first instanceof HTMLElement ? first.dataset.fbAnchor || '' : '';
  }

  /** @param {string} [message] */
  #renderError(message) {
    this.#out.innerHTML = `
      <p class="fb__error" role="alert">${esc(message || this.#strings.errGeneric)}</p>
      <button type="button" class="fb__btn2" data-fb-retry>${esc(this.#strings.retry)}</button>`;
  }

  #render() {
    const fit = this.#fit;
    if (!fit) return;

    const pieces = fit.pieces ?? [];
    const totalMinor = pieces.reduce(
      (/** @type {number} */ sum, /** @type {any} */ piece) => sum + this.#priceMinorFor(piece),
      0
    );
    const rate = Number(this.dataset.discountRate || 0);
    const qualifies = rate > 0 && pieces.length >= Number(this.dataset.discountMin || 3);
    const savingMinor = qualifies ? Math.round(totalMinor * rate) : 0;

    this.#out.innerHTML = `
      ${fit.reply ? `<p class="fb__reply">${esc(fit.reply)}</p>` : ''}
      ${fit.headline ? `<p class="fb__intro">${esc(fit.headline)}</p>` : ''}

      <ul class="fb__grid">${pieces.map((/** @type {any} */ p) => this.#card(p)).join('')}</ul>

      <div class="fb__foot">
        <div class="fb__tot">
          <span class="fb__tot-n">${esc(pieces.length)} ${esc(this.#strings.pieces)}</span>
          <span class="${qualifies ? 'fb__was' : 'fb__now'}">${esc(this.#money(totalMinor))}</span>
          ${qualifies ? `<span class="fb__now">${esc(this.#money(totalMinor - savingMinor))}</span>` : ''}
        </div>
        ${
          qualifies
            ? `<span class="fb__save">${esc(
                (this.#strings.saveWith || '').replace('[amount]', this.#money(savingMinor))
              )}</span>`
            : ''
        }
        <div class="fb__foot-a">
          <button type="button" class="fb__cta" data-fb-add-outfit>${esc(this.#strings.addOutfit)}</button>
          <button type="button" class="fb__btn2" data-fb-shuffle>${esc(this.#strings.shuffle)}</button>
        </div>
        ${
          this.dataset.allowRefine === 'true'
            ? `<div class="fb__refine">
                 <label class="visually-hidden" for="fb-refine-${this.id}">${esc(this.#strings.refinePlaceholder)}</label>
                 <input id="fb-refine-${this.id}" class="fb__input" type="text" maxlength="200"
                        autocomplete="off" placeholder="${esc(this.#strings.refinePlaceholder)}" data-fb-refine>
                 <button type="button" class="fb__btn2" data-fb-refine-go>${esc(this.#strings.refineGo)}</button>
               </div>`
            : ''
        }
      </div>`;
  }

  /**
   * @param {any} piece
   * @returns {string}
   */
  #card(piece) {
    const locked = this.#locked.has(piece.handle);
    const variants = piece.variants ?? [];
    const soldOut = !variants.some((/** @type {any} */ v) => v.available);
    const swappable = this.dataset.allowSwap === 'true';

    return `
      <li class="fb__card" data-fb-card data-handle="${esc(piece.handle)}" data-slot="${esc(piece.slot)}">
        <a class="fb__media" href="/products/${esc(piece.handle)}">
          ${
            piece.image
              ? `<img src="${esc(piece.image)}" alt="" loading="lazy" width="300" height="300">`
              : '<span class="fb__blank"></span>'
          }
        </a>

        <span class="fb__slot">${esc(SLOT_LABELS[piece.slot] || piece.slot)}</span>
        <a class="fb__title" href="/products/${esc(piece.handle)}">${esc(piece.title)}</a>
        <span class="fb__price">${esc(this.#money(this.#priceMinorFor(piece)))}</span>
        ${this.#sizes(piece)}

        ${/* Swap, Lock and Add share one row at 25:25:50. */ ''}
        <div class="fb__actions${swappable ? '' : ' fb__actions--no-swap'}">
          ${
            swappable
              ? `<button type="button" class="fb__icon" data-fb-swap
                         title="${esc(this.#strings.swap)}" aria-label="${esc(this.#strings.swap)}">${ICON_SWAP}</button>`
              : ''
          }
          <button type="button" class="fb__icon" data-fb-lock aria-pressed="${locked}"
                  title="${esc(locked ? this.#strings.locked : this.#strings.lock)}"
                  aria-label="${esc(locked ? this.#strings.locked : this.#strings.lock)}">${ICON_LOCK}</button>
          <button type="button" class="fb__add" data-fb-add ${soldOut ? 'disabled' : ''}>
            ${esc(soldOut ? this.#strings.soldOut : this.#strings.addPiece)}
          </button>
        </div>
      </li>`;
  }

  /**
   * Sizes as pills rather than a dropdown. A sneaker can carry 18 variants, so
   * the tail collapses behind a +N until the customer asks for it — otherwise
   * one card towers over the other three.
   * @param {any} piece
   * @returns {string}
   */
  #sizes(piece) {
    const variants = piece.variants ?? [];
    if (variants.length < 2) return '';

    const chosen = this.#chosen.get(piece.handle);
    const showAll = this.#expanded.has(piece.handle);

    // Apparel sizes are 1-3 characters and four fit a row. Footwear carries
    // labels like "5.5Y (7W)" that are three times as wide, so the same count
    // would wrap. Sizing off the longest label keeps every card to one line.
    const longest = variants.reduce((max, v) => Math.max(max, String(v.title).length), 0);
    const perRow = longest > 4 ? 2 : SIZES_PER_ROW;
    // Leave the last slot for the +N unless everything already fits.
    const limit = variants.length <= perRow ? perRow : perRow - 1;

    let shown = showAll ? variants : variants.slice(0, limit);
    // The selected size must always be on show, or the customer sees no pill
    // pressed and cannot tell what they are about to add.
    const chosenIndex = variants.findIndex((/** @type {any} */ v) => v.id === chosen);
    if (!showAll && chosenIndex >= limit) {
      shown = [variants[chosenIndex], ...variants.slice(0, Math.max(0, limit - 1))];
    }
    const rest = variants.length - shown.length;

    return `
      <div class="fb__sizes" role="group" aria-label="Size">
        ${shown
          .map(
            (/** @type {any} */ v) => `
            <button type="button" class="fb__size" data-fb-size="${esc(v.id)}"
                    aria-pressed="${v.id === chosen}" ${v.available ? '' : 'disabled'}>
              ${esc(v.title)}
            </button>`
          )
          .join('')}
        ${rest > 0 ? `<button type="button" class="fb__size fb__size--more" data-fb-more>+${rest}</button>` : ''}
      </div>`;
  }

  // ------------------------------------------------------------------ actions

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    /** @param {string} attribute */
    const hit = (attribute) => target.closest(`[${attribute}]`);

    const pick = hit('data-fb-pick');
    if (pick instanceof HTMLElement) {
      const input = this.querySelector('[data-fb-input]');
      if (input instanceof HTMLInputElement) input.value = pick.dataset.fbTitle || '';
      this.#anchor = pick.dataset.fbPick || '';
      this.#locked.clear();
      this.#seen.clear();
      this.#chosen.clear();
      this.#expanded.clear();
      this.#slots = null;
      this.#closeSuggest();
      this.#build();
      return;
    }

    // Page rail: preference chips and the two feeds.
    for (const attr of ['data-fb-pref-slot', 'data-fb-pref-brand', 'data-fb-pref-colour', 'data-fb-pref-style']) {
      const pref = hit(attr);
      if (pref instanceof HTMLElement) {
        pref.setAttribute('aria-pressed', pref.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
        return;
      }
    }
    if (hit('data-fb-pref-save')) return void this.#savePreferences();

    // Brand dropdown. Apply closes and rebuilds; Clear only unticks, so the
    // customer can change their mind before committing.
    if (hit('data-fb-drop-apply')) {
      const drop = this.querySelector('[data-fb-drop]');
      if (drop instanceof HTMLDetailsElement) drop.open = false;
      this.#syncBrandLabel();
      return void this.#savePreferences();
    }
    if (hit('data-fb-drop-clear')) {
      for (const box of this.querySelectorAll('[data-fb-pref-brand]')) {
        if (box instanceof HTMLInputElement) box.checked = false;
      }
      this.#syncBrandLabel();
      return;
    }

    const feedPick = hit('data-fb-anchor');
    if (feedPick instanceof HTMLElement) {
      this.#anchor = feedPick.dataset.fbAnchor || '';
      this.#locked.clear();
      this.#seen.clear();
      this.#chosen.clear();
      this.#expanded.clear();
      this.#out.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return void this.#build();
    }

    const chip = hit('data-fb-slot');
    if (chip instanceof HTMLElement) return this.#toggleSlot(chip);

    if (hit('data-fb-retry') || hit('data-fb-shuffle')) return void this.#build({ shuffle: true });
    if (hit('data-fb-refine-go')) return void this.#refine();

    const outfit = hit('data-fb-add-outfit');
    if (outfit instanceof HTMLElement) return void this.#addOutfit(outfit);

    // The swap panel is a sibling of its card, not a descendant, so resolve back
    // to the card through the panel when the click landed inside one.
    const panel = target.closest('[data-fb-swap-panel]');
    const card =
      panel instanceof HTMLElement
        ? this.querySelector(`[data-fb-card][data-handle="${CSS.escape(panel.dataset.fbFor || '')}"]`)
        : target.closest('[data-fb-card]');
    if (!(card instanceof HTMLElement)) return;

    const add = hit('data-fb-add');
    if (add instanceof HTMLElement) return void this.#addPiece(card, add);

    if (hit('data-fb-lock')) {
      event.preventDefault();
      return this.#toggleLock(card);
    }
    if (hit('data-fb-swap')) {
      event.preventDefault();
      return void this.#openSwap(card);
    }
    if (hit('data-fb-swap-close')) return this.#closeSwap(card);

    const size = hit('data-fb-size');
    if (size instanceof HTMLElement) return this.#chooseSize(card, size);

    if (hit('data-fb-more')) {
      if (card.dataset.handle) this.#expanded.add(card.dataset.handle);
      return this.#render();
    }

    const alt = hit('data-fb-alt');
    if (alt instanceof HTMLElement) {
      event.preventDefault();
      return this.#takeAlternative(card, alt);
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Enter') return;
    const target = event.target;
    if (target instanceof HTMLInputElement && target.hasAttribute('data-fb-refine')) {
      event.preventDefault();
      this.#refine();
    }
  };

  #refine() {
    const input = this.querySelector('[data-fb-refine]');
    if (!(input instanceof HTMLInputElement)) return;
    const instruction = input.value.trim();
    if (!instruction) return;
    input.value = '';
    this.#build({ instruction });
  }

  /**
   * @param {HTMLElement} card
   * @param {HTMLElement} button
   */
  #chooseSize(card, button) {
    const handle = card.dataset.handle;
    const id = button.dataset.fbSize;
    if (!handle || !id) return;
    this.#chosen.set(handle, id);

    for (const pill of card.querySelectorAll('[data-fb-size]')) {
      pill.setAttribute('aria-pressed', String(pill === button));
    }

    // Patch the two figures the choice can move rather than re-rendering, which
    // would close an open swap panel and drop focus off the pill.
    const piece = (this.#fit?.pieces ?? []).find((/** @type {any} */ p) => p.handle === handle);
    const price = card.querySelector('.fb__price');
    if (piece && price) price.textContent = this.#money(this.#priceMinorFor(piece));
    this.#refreshTotals();
  }

  /** Recompute the footer figures in place after a size change. */
  #refreshTotals() {
    const pieces = this.#fit?.pieces ?? [];
    const totalMinor = pieces.reduce(
      (/** @type {number} */ sum, /** @type {any} */ piece) => sum + this.#priceMinorFor(piece),
      0
    );
    const rate = Number(this.dataset.discountRate || 0);
    const qualifies = rate > 0 && pieces.length >= Number(this.dataset.discountMin || 3);
    const savingMinor = qualifies ? Math.round(totalMinor * rate) : 0;

    const was = this.querySelector('.fb__was');
    const now = this.querySelector('.fb__now');
    const saving = this.querySelector('.fb__save');
    if (was) was.textContent = this.#money(totalMinor);
    if (now) now.textContent = this.#money(qualifies ? totalMinor - savingMinor : totalMinor);
    if (saving) saving.textContent = (this.#strings.saveWith || '').replace('[amount]', this.#money(savingMinor));
  }

  /** @param {HTMLElement} card */
  #toggleLock(card) {
    const handle = card.dataset.handle;
    if (!handle) return;

    if (this.#locked.has(handle)) {
      this.#locked.delete(handle);
    } else {
      if (this.#locked.size >= MAX_LOCKS) {
        // Locking everything would leave the engine nothing to shuffle.
        this.#flash(card, `Unlock one first — ${MAX_LOCKS} is the most you can keep.`);
        return;
      }
      this.#locked.add(handle);
    }

    const locked = this.#locked.has(handle);
    const button = card.querySelector('[data-fb-lock]');
    if (button instanceof HTMLElement) {
      const label = locked ? this.#strings.locked : this.#strings.lock;
      button.setAttribute('aria-pressed', String(locked));
      button.setAttribute('aria-label', label);
      button.setAttribute('title', label);
    }
  }

  /**
   * Swap one piece. Hits `/alternatives`, which runs no model call, so it comes
   * back in a fraction of the time a full rebuild takes.
   * @param {HTMLElement} card
   */
  async #openSwap(card) {
    if (this.#panelFor(card)) return this.#closeSwap(card);
    const slot = card.dataset.slot;
    if (!slot || !this.#anchor) return;

    // A sibling row spanning the whole grid, not a child of the card: a 3x3 grid
    // squeezed into one column gives thumbnails too small to choose from.
    const panel = document.createElement('li');
    panel.className = 'fb__swap-row';
    panel.setAttribute('data-fb-swap-panel', '');
    panel.dataset.fbFor = card.dataset.handle || '';
    panel.innerHTML = '<p class="fb__swap-msg">Finding alternatives…</p>';
    card.after(panel);

    /** @type {Record<string, string>} */
    const headers = { 'Content-Type': 'application/json', 'X-Fit-Visitor': visitorId() };
    if (this.dataset.accessKey) headers['X-Fit-Key'] = this.dataset.accessKey;

    try {
      const response = await fetch(`${this.dataset.endpoint}/alternatives`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          anchor_handle: this.#anchor,
          slot,
          // Nothing already in the fit, so every tile is a real change.
          exclude: (this.#fit?.pieces ?? []).map((/** @type {any} */ piece) => piece.handle),
        }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.error || 'unavailable');

      const options = (data.pieces ?? []).slice(0, ALT_COLUMNS * ALT_COLUMNS);
      if (!options.length) {
        panel.innerHTML = '<p class="fb__swap-msg">Nothing else in this category right now.</p>';
        return;
      }
      await this.#localise(options);

      panel.innerHTML = `
        <div class="fb__alts">
          ${options
            .map(
              (/** @type {any} */ option) => `
              <button type="button" class="fb__alt" data-fb-alt="${esc(option.handle)}">
                ${
                  option.image
                    ? `<img src="${esc(option.image)}" alt="" loading="lazy" width="120" height="120">`
                    : '<span class="fb__blank"></span>'
                }
                <span class="fb__alt-title">${esc(option.title)}</span>
                <span class="fb__alt-price">${esc(this.#money(this.#priceMinorFor(option)))}</span>
              </button>`
            )
            .join('')}
        </div>
        <button type="button" class="fb__btn2" data-fb-swap-close>Close</button>`;

      // Stash the payloads so taking one needs no second request.
      panel.dataset.fbOptions = JSON.stringify(options);
    } catch {
      panel.innerHTML = '<p class="fb__swap-msg">Could not load alternatives.</p>';
    }
  }

  /**
   * @param {HTMLElement} card
   * @returns {HTMLElement | null}
   */
  #panelFor(card) {
    const next = card.nextElementSibling;
    return next instanceof HTMLElement && next.hasAttribute('data-fb-swap-panel') ? next : null;
  }

  /** @param {HTMLElement} card */
  #closeSwap(card) {
    this.#panelFor(card)?.remove();
  }

  /**
   * @param {HTMLElement} card
   * @param {HTMLElement} trigger
   */
  #takeAlternative(card, trigger) {
    const panel = this.#panelFor(card);
    if (!panel) return;

    /** @type {any[]} */
    let options = [];
    try {
      options = JSON.parse(panel.dataset.fbOptions || '[]');
    } catch {
      return;
    }

    const replacement = options.find((option) => option.handle === trigger.dataset.fbAlt);
    const index = (this.#fit?.pieces ?? []).findIndex((/** @type {any} */ p) => p.handle === card.dataset.handle);
    if (!replacement || index < 0) return;

    const outgoing = this.#fit.pieces[index].handle;
    this.#fit.pieces[index] = replacement;
    this.#setDefaultVariant(replacement);
    this.#chosen.delete(outgoing);
    this.#expanded.delete(outgoing);
    this.#seen.add(replacement.handle);
    // A hand-picked piece is one the customer chose, so it survives a shuffle.
    this.#locked.delete(outgoing);
    if (this.#locked.size < MAX_LOCKS) this.#locked.add(replacement.handle);
    this.#render();
  }

  // --------------------------------------------------------------------- cart

  /**
   * @param {HTMLElement} card
   * @param {HTMLElement} button
   */
  async #addPiece(card, button) {
    const piece = (this.#fit?.pieces ?? []).find((/** @type {any} */ p) => p.handle === card.dataset.handle);
    if (!piece) return;
    const item = this.#lineFor(piece);
    if (!item) return this.#flash(card, this.#strings.soldOut);
    await this.#addToCart([item], button, card);
  }

  /** @param {HTMLElement} button */
  async #addOutfit(button) {
    const items = (this.#fit?.pieces ?? [])
      .map((/** @type {any} */ piece) => this.#lineFor(piece))
      .filter(Boolean);
    if (!items.length) return;
    await this.#addToCart(/** @type {any[]} */ (items), button);
  }

  /**
   * One cart line. The underscore-prefixed properties are hidden from the
   * customer and the order confirmation, and exist for two reasons: the Shopify
   * discount function keys the full-outfit discount on `_fit_id`, and they make
   * Fit Builder revenue reportable in Shopify without a separate pipeline.
   * @param {any} piece
   */
  #lineFor(piece) {
    const chosen = this.#chosen.get(piece.handle);
    const variants = piece.variants ?? [];
    const variant =
      variants.find((/** @type {any} */ v) => v.id === chosen && v.available) ??
      variants.find((/** @type {any} */ v) => v.available);
    const id = variant ? variantId(variant.id) : '';
    if (!id) return null;

    return {
      id,
      quantity: 1,
      properties: {
        _fit_id: this.#fitId,
        _fit_slot: piece.slot,
        _fit_anchor: this.#anchor,
        _fit_placement: this.dataset.source || '',
      },
    };
  }

  /**
   * @param {any[]} items
   * @param {HTMLElement} button
   * @param {HTMLElement} [card]
   */
  async #addToCart(items, button, card) {
    const label = button.textContent;
    const restore = () => {
      button.textContent = label;
      button.removeAttribute('disabled');
    };
    button.setAttribute('disabled', '');
    button.textContent = this.#strings.adding;

    // Ask Shopify to re-render the cart sections in the same response, which is
    // what the theme's own cart components expect to receive.
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean);

    let data = null;
    try {
      const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          ...(sectionIds.length ? { sections: sectionIds.join(','), sections_url: window.location.pathname } : {}),
        }),
      });
      data = await response.json().catch(() => null);

      // `/cart/add.js` reports its own failures with a `status` field and a 4xx,
      // most often because the last unit sold between the fit being built and
      // the customer tapping add.
      if (!response.ok || data?.status) throw new Error(data?.description || data?.message || '');
    } catch (error) {
      document.dispatchEvent(
        new CartAddEvent({}, this.id, { didError: true, source: 'fit-builder', itemCount: items.length })
      );
      this.#flash(card ?? button.parentElement ?? button, /** @type {any} */ (error)?.message || 'Could not add to cart.');
      restore();
      return;
    }

    // `source: 'product-form-component'` is not a mislabel for the cart's
    // purposes: it is the flag the cart bubble reads to mean "add this to the
    // count" rather than "replace the count", which is what happened. Our own
    // source string would make the bubble read 1 after adding 4.
    document.dispatchEvent(
      new CartAddEvent({}, this.id, {
        source: 'product-form-component',
        itemCount: items.length,
        sections: data?.sections,
      })
    );

    button.textContent = this.#strings.added;
    setTimeout(restore, ADDED_FLASH_MS);
  }

  /**
   * @param {HTMLElement} host
   * @param {string} message
   */
  #flash(host, message) {
    host.querySelector('[data-fb-note]')?.remove();
    const note = document.createElement('p');
    note.className = 'fb__note';
    note.setAttribute('data-fb-note', '');
    note.setAttribute('role', 'status');
    note.textContent = message;
    host.appendChild(note);
    setTimeout(() => note.remove(), 4000);
  }
}

if (!customElements.get('fit-builder')) {
  customElements.define('fit-builder', FitBuilder);
}

export { FitBuilder };
